import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import {
  Package, Plus, Edit2, Trash2, Search, RefreshCw, Monitor, Laptop, 
  Keyboard, Mouse, Headphones, Car, Building2, CreditCard, Users,
  AlertTriangle, CheckCircle, Clock, Wrench, FileText, Calendar,
  IndianRupee, Shield, TrendingDown, ArrowRight, Eye, History,
  Settings, Tag, Box, UserCheck, UserX, Download, CheckCircle2, X, Upload
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../api';

const ASSET_STATUS = {
  AVAILABLE: { label: 'Available', color: 'bg-green-100 text-green-700' },
  ASSIGNED: { label: 'Assigned', color: 'bg-blue-100 text-blue-700' },
  MAINTENANCE: { label: 'Under Maintenance', color: 'bg-amber-100 text-amber-700' },
  DAMAGED: { label: 'Damaged', color: 'bg-red-100 text-red-700' },
  RETIRED: { label: 'Retired', color: 'bg-gray-100 text-gray-700' },
  LOST: { label: 'Lost', color: 'bg-red-100 text-red-700' }
};

const CONDITION_OPTIONS = ['Excellent', 'Good', 'Fair', 'Poor'];

const DEFAULT_CATEGORIES = [
  { name: 'IT Equipment', icon: 'Monitor', types: ['Laptop', 'Desktop', 'Monitor', 'Keyboard', 'Mouse', 'Headset', 'Webcam', 'Printer'] },
  { name: 'Office Equipment', icon: 'Building2', types: ['Desk', 'Chair', 'Locker', 'Cabinet', 'Whiteboard'] },
  { name: 'Access & ID', icon: 'CreditCard', types: ['ID Card', 'Access Card', 'Parking Card', 'Keys'] },
  { name: 'Vehicles', icon: 'Car', types: ['Company Car', 'Two Wheeler', 'Company Bike'] },
  { name: 'Communication', icon: 'Headphones', types: ['Mobile Phone', 'SIM Card', 'Landline'] },
  { name: 'Other', icon: 'Package', types: ['Other'] }
];

const AssetManagement = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  
  // Data states
  const [assetTypes, setAssetTypes] = useState([]);
  const [assets, setAssets] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [maintenanceRecords, setMaintenanceRecords] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [dashboardStats, setDashboardStats] = useState(null);
  
  // Dialog states
  const [assetTypeDialogOpen, setAssetTypeDialogOpen] = useState(false);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [maintenanceDialogOpen, setMaintenanceDialogOpen] = useState(false);
  const [viewAssetDialogOpen, setViewAssetDialogOpen] = useState(false);
  const [bulkUploadOpen, setBulkUploadOpen] = useState(false);
  const [bulkUploading, setBulkUploading] = useState(false);
  
  // Edit states
  const [editingAssetType, setEditingAssetType] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  
  // Form states
  const [assetTypeForm, setAssetTypeForm] = useState({
    name: '', category: '', description: '', depreciation_rate: '10', icon: 'Package'
  });
  
  const [assetForm, setAssetForm] = useState({
    asset_type_id: '', name: '', serial_number: '', model: '', brand: '',
    purchase_date: '', purchase_price: '', vendor: '', warranty_expiry: '',
    condition: 'Good', location: '', notes: '',
    insurance_provider: '', insurance_policy_number: '', insurance_expiry: ''
  });
  
  const [assignForm, setAssignForm] = useState({
    employee_id: '', assigned_date: new Date().toISOString().split('T')[0],
    notes: '', expected_return_date: ''
  });
  
  const [returnForm, setReturnForm] = useState({
    return_date: new Date().toISOString().split('T')[0],
    condition: 'Good', notes: ''
  });
  
  const [maintenanceForm, setMaintenanceForm] = useState({
    maintenance_type: 'Repair', description: '', cost: '', vendor: '',
    start_date: new Date().toISOString().split('T')[0], end_date: '', notes: ''
  });
  
  // Filter states
  const [searchQuery, setSearchQuery] = useState('');
  const [assignmentSearch, setAssignmentSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterType, setFilterType] = useState('all');

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        fetchAssetTypes(),
        fetchAssets(),
        fetchAssignments(),
        fetchMaintenanceRecords(),
        fetchEmployees(),
        fetchDashboardStats()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchAssetTypes = async () => {
    try {
      const response = await api.get('/assets/types');
      setAssetTypes(response.data);
    } catch (error) {
      console.error('Asset types error:', error);
    }
  };

  const fetchAssets = async () => {
    try {
      const response = await api.get('/assets');
      setAssets(response.data);
    } catch (error) {
      console.error('Assets error:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const response = await api.get('/assets/assignments');
      setAssignments(response.data);
    } catch (error) {
      console.error('Assignments error:', error);
    }
  };

  const fetchMaintenanceRecords = async () => {
    try {
      const response = await api.get('/assets/maintenance');
      setMaintenanceRecords(response.data);
    } catch (error) {
      console.error('Maintenance error:', error);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await api.get('/employees');
      setEmployees(response.data.filter(e => !e.status || e.status === 'Active' || e.status === 'active'));
    } catch (error) {
      console.error('Employees error:', error);
    }
  };

  const fetchDashboardStats = async () => {
    try {
      const response = await api.get('/assets/dashboard');
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Dashboard stats error:', error);
    }
  };

  // Asset Type handlers
  const handleSaveAssetType = async () => {
    if (!assetTypeForm.name || !assetTypeForm.category) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      if (editingAssetType) {
        await api.put(`/assets/types/${editingAssetType.id}`, assetTypeForm);
        toast.success('Asset type updated successfully');
      } else {
        await api.post('/assets/types', assetTypeForm);
        toast.success('Asset type created successfully');
      }
      setAssetTypeDialogOpen(false);
      resetAssetTypeForm();
      fetchAssetTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save asset type');
    }
  };

  const handleDeleteAssetType = async (assetType) => {
    if (!window.confirm(`Delete asset type "${assetType.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/assets/types/${assetType.id}`);
      toast.success('Asset type deleted');
      fetchAssetTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete asset type');
    }
  };

  const editAssetType = (assetType) => {
    setEditingAssetType(assetType);
    setAssetTypeForm({
      name: assetType.name,
      category: assetType.category,
      description: assetType.description || '',
      depreciation_rate: assetType.depreciation_rate?.toString() || '10',
      icon: assetType.icon || 'Package'
    });
    setAssetTypeDialogOpen(true);
  };

  const resetAssetTypeForm = () => {
    setEditingAssetType(null);
    setAssetTypeForm({ name: '', category: '', description: '', depreciation_rate: '10', icon: 'Package' });
  };

  // Asset handlers
  const handleSaveAsset = async () => {
    if (!assetForm.asset_type_id || !assetForm.name || !assetForm.serial_number) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      const payload = {
        ...assetForm,
        purchase_price: parseFloat(assetForm.purchase_price) || 0
      };
      
      if (editingAsset) {
        await api.put(`/assets/${editingAsset.id}`, payload);
        toast.success('Asset updated successfully');
      } else {
        await api.post('/assets', payload);
        toast.success('Asset added successfully');
      }
      setAssetDialogOpen(false);
      resetAssetForm();
      fetchAssets();
      fetchDashboardStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save asset');
    }
  };

  const handleDeleteAsset = async (asset) => {
    if (!window.confirm(`Delete asset "${asset.name}"? This cannot be undone.`)) return;
    
    try {
      await api.delete(`/assets/${asset.id}`);
      toast.success('Asset deleted');
      fetchAssets();
      fetchDashboardStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete asset');
    }
  };

  const editAsset = (asset) => {
    setEditingAsset(asset);
    setAssetForm({
      asset_type_id: asset.asset_type_id,
      name: asset.name,
      serial_number: asset.serial_number,
      model: asset.model || '',
      brand: asset.brand || '',
      purchase_date: asset.purchase_date || '',
      purchase_price: asset.purchase_price?.toString() || '',
      vendor: asset.vendor || '',
      warranty_expiry: asset.warranty_expiry || '',
      condition: asset.condition || 'Good',
      location: asset.location || '',
      notes: asset.notes || '',
      insurance_provider: asset.insurance_provider || '',
      insurance_policy_number: asset.insurance_policy_number || '',
      insurance_expiry: asset.insurance_expiry || ''
    });
    setAssetDialogOpen(true);
  };

  const handleBulkUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post('/assets/bulk/upload', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success(`${res.data.created} assets created, ${res.data.skipped} skipped`);
      if (res.data.errors?.length > 0) {
        res.data.errors.slice(0, 5).forEach(err => toast.warning(err));
      }
      setBulkUploadOpen(false);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Bulk upload failed');
    } finally {
      setBulkUploading(false);
      e.target.value = '';
    }
  };

  const downloadTemplate = async () => {
    try {
      const res = await api.get('/assets/bulk/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement('a');
      a.href = url;
      a.download = 'asset_bulk_upload_template.xlsx';
      a.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('Failed to download template');
    }
  };

  const resetAssetForm = () => {
    setEditingAsset(null);
    setAssetForm({
      asset_type_id: '', name: '', serial_number: '', model: '', brand: '',
      purchase_date: '', purchase_price: '', vendor: '', warranty_expiry: '',
      condition: 'Good', location: '', notes: '',
      insurance_provider: '', insurance_policy_number: '', insurance_expiry: ''
    });
  };

  // Assignment handlers
  const handleAssignAsset = async () => {
    if (!assignForm.employee_id) {
      toast.error('Please select an employee');
      return;
    }
    
    try {
      await api.post(`/assets/${selectedAsset.id}/assign`, assignForm);
      toast.success('Asset assigned successfully');
      setAssignDialogOpen(false);
      setSelectedAsset(null);
      setAssignForm({
        employee_id: '', assigned_date: new Date().toISOString().split('T')[0],
        notes: '', expected_return_date: ''
      });
      fetchAssets();
      fetchAssignments();
      fetchDashboardStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to assign asset');
    }
  };

  const handleReturnAsset = async () => {
    try {
      await api.post(`/assets/assignments/${selectedAssignment.id}/return`, returnForm);
      toast.success('Asset returned successfully');
      setReturnDialogOpen(false);
      setSelectedAssignment(null);
      setReturnForm({
        return_date: new Date().toISOString().split('T')[0],
        condition: 'Good', notes: ''
      });
      fetchAssets();
      fetchAssignments();
      fetchDashboardStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to return asset');
    }
  };

  // Maintenance handlers
  const handleSaveMaintenance = async () => {
    if (!maintenanceForm.description) {
      toast.error('Please enter maintenance description');
      return;
    }
    
    try {
      await api.post(`/assets/${selectedAsset.id}/maintenance`, {
        ...maintenanceForm,
        cost: parseFloat(maintenanceForm.cost) || 0
      });
      toast.success('Maintenance record added');
      setMaintenanceDialogOpen(false);
      setMaintenanceForm({
        maintenance_type: 'Repair', description: '', cost: '', vendor: '',
        start_date: new Date().toISOString().split('T')[0], end_date: '', notes: ''
      });
      fetchMaintenanceRecords();
      fetchAssets();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add maintenance record');
    }
  };

  // Utility functions
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(amount || 0);
  };

  const getAssetTypeName = (typeId) => {
    const type = assetTypes.find(t => t.id === typeId);
    return type?.name || 'Unknown';
  };

  const getEmployeeName = (empId) => {
    const emp = employees.find(e => e.id === empId);
    return emp ? `${emp.first_name} ${emp.last_name}` : 'Unknown';
  };

  const calculateDepreciation = (asset) => {
    if (!asset.purchase_price || !asset.purchase_date) return 0;
    const assetType = assetTypes.find(t => t.id === asset.asset_type_id);
    const rate = assetType?.depreciation_rate || 10;
    const purchaseDate = new Date(asset.purchase_date);
    const years = (new Date() - purchaseDate) / (365.25 * 24 * 60 * 60 * 1000);
    const depreciation = asset.purchase_price * (rate / 100) * years;
    return Math.min(depreciation, asset.purchase_price);
  };

  const getCurrentValue = (asset) => {
    return Math.max(0, (asset.purchase_price || 0) - calculateDepreciation(asset));
  };

  const filteredAssets = assets.filter(asset => {
    const matchesSearch = !searchQuery || 
      asset.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.serial_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      asset.brand?.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = filterStatus === 'all' || asset.status === filterStatus;
    const matchesType = filterType === 'all' || asset.asset_type_id === filterType;
    return matchesSearch && matchesStatus && matchesType;
  });

  const getIconComponent = (iconName) => {
    const icons = { Monitor, Laptop, Keyboard, Mouse, Headphones, Car, Building2, CreditCard, Package, Box, Settings };
    return icons[iconName] || Package;
  };

  if (loading) {
    return (
      <Layout user={user} setUser={setUser}>
        <div className="flex items-center justify-center h-64">
          <RefreshCw className="animate-spin text-indigo-600" size={32} />
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} setUser={setUser}>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Asset Management</h1>
            <p className="text-slate-500">Track and manage company assets</p>
          </div>
          <Button onClick={fetchAllData} variant="outline" className="gap-2">
            <RefreshCw size={16} /> Refresh
          </Button>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-slate-100">
            <TabsTrigger value="dashboard" className="gap-2"><Package size={16} /> Dashboard</TabsTrigger>
            <TabsTrigger value="assets" className="gap-2"><Box size={16} /> Assets</TabsTrigger>
            <TabsTrigger value="types" className="gap-2"><Tag size={16} /> Asset Types</TabsTrigger>
            <TabsTrigger value="assignments" className="gap-2"><UserCheck size={16} /> Assignments</TabsTrigger>
            <TabsTrigger value="maintenance" className="gap-2"><Wrench size={16} /> Maintenance</TabsTrigger>
          </TabsList>

          {/* Dashboard Tab */}
          <TabsContent value="dashboard" className="space-y-4">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
              <Card className="bg-gradient-to-br from-blue-50 to-blue-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-blue-500 rounded-lg">
                      <Package className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-blue-700">{dashboardStats?.total_assets || 0}</p>
                      <p className="text-xs text-blue-600">Total Assets</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-green-50 to-green-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-green-500 rounded-lg">
                      <CheckCircle className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-green-700">{dashboardStats?.available || 0}</p>
                      <p className="text-xs text-green-600">Available</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-indigo-50 to-indigo-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-indigo-500 rounded-lg">
                      <Users className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-indigo-700">{dashboardStats?.assigned || 0}</p>
                      <p className="text-xs text-indigo-600">Assigned</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-amber-50 to-amber-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-amber-500 rounded-lg">
                      <Wrench className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-amber-700">{dashboardStats?.maintenance || 0}</p>
                      <p className="text-xs text-amber-600">Maintenance</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-purple-50 to-purple-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-purple-500 rounded-lg">
                      <IndianRupee className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="text-lg font-bold text-purple-700">{formatCurrency(dashboardStats?.total_value || 0)}</p>
                      <p className="text-xs text-purple-600">Total Value</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
              
              <Card className="bg-gradient-to-br from-red-50 to-red-100">
                <CardContent className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-red-500 rounded-lg">
                      <AlertTriangle className="text-white" size={20} />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-red-700">{dashboardStats?.warranty_expiring || 0}</p>
                      <p className="text-xs text-red-600">Warranty Expiring</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Asset Distribution */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Assets by Category</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {dashboardStats?.by_category?.map((cat, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            {React.createElement(getIconComponent(cat.icon), { size: 18, className: 'text-indigo-600' })}
                          </div>
                          <span className="font-medium">{cat.category}</span>
                        </div>
                        <Badge variant="outline">{cat.count} assets</Badge>
                      </div>
                    )) || <p className="text-slate-500 text-center py-4">No categories yet</p>}
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Assignments</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {assignments.slice(0, 5).map((assignment, idx) => (
                      <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                        <div>
                          <p className="font-medium">{assignment.asset_name}</p>
                          <p className="text-sm text-slate-500">{assignment.employee_name}</p>
                        </div>
                        <Badge className={assignment.returned_date ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                          {assignment.returned_date ? 'Returned' : 'Active'}
                        </Badge>
                      </div>
                    ))}
                    {assignments.length === 0 && <p className="text-slate-500 text-center py-4">No assignments yet</p>}
                  </div>
                </CardContent>
              </Card>
            </div>
          </TabsContent>

          {/* Assets Tab */}
          <TabsContent value="assets" className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-2 flex-1">
                <div className="relative flex-1 max-w-md">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                  <Input
                    placeholder="Search assets..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Status</SelectItem>
                    {Object.entries(ASSET_STATUS).map(([key, { label }]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="w-40">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    {assetTypes.map(type => (
                      <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={() => { resetAssetForm(); setAssetDialogOpen(true); }} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
                <Plus size={16} /> Add Asset
              </Button>
              <Button onClick={() => setBulkUploadOpen(true)} variant="outline" className="gap-2">
                <Upload size={16} /> Bulk Upload
              </Button>
            </div>

            <Card>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset</TableHead>
                      <TableHead>Serial Number</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Assigned To</TableHead>
                      <TableHead>Condition</TableHead>
                      <TableHead>Current Value</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredAssets.map(asset => (
                      <TableRow key={asset.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{asset.name}</p>
                            <p className="text-sm text-slate-500">{asset.brand} {asset.model}</p>
                          </div>
                        </TableCell>
                        <TableCell className="font-mono text-sm">{asset.serial_number}</TableCell>
                        <TableCell>{getAssetTypeName(asset.asset_type_id)}</TableCell>
                        <TableCell>
                          <Badge className={ASSET_STATUS[asset.status]?.color || 'bg-gray-100'}>
                            {ASSET_STATUS[asset.status]?.label || asset.status}
                          </Badge>
                        </TableCell>
                        <TableCell>{asset.assigned_to_name || '-'}</TableCell>
                        <TableCell>{asset.condition}</TableCell>
                        <TableCell>{formatCurrency(getCurrentValue(asset))}</TableCell>
                        <TableCell>
                          <div className="flex justify-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedAsset(asset); setViewAssetDialogOpen(true); }} title="View Details">
                              <Eye size={16} />
                            </Button>
                            {asset.status === 'AVAILABLE' && (
                              <Button variant="ghost" size="sm" onClick={() => { setSelectedAsset(asset); setAssignDialogOpen(true); }} title="Assign" className="text-green-600 hover:text-green-700">
                                <UserCheck size={16} />
                              </Button>
                            )}
                            <Button variant="ghost" size="sm" onClick={() => { setSelectedAsset(asset); setMaintenanceDialogOpen(true); }} title="Maintenance" className="text-amber-600 hover:text-amber-700">
                              <Wrench size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => editAsset(asset)} title="Edit">
                              <Edit2 size={16} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteAsset(asset)} title="Delete" className="text-red-500 hover:text-red-700">
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                    {filteredAssets.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No assets found. Add your first asset to get started.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Asset Types Tab */}
          <TabsContent value="types" className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-slate-600">Manage asset categories and types</p>
              <Button onClick={() => { resetAssetTypeForm(); setAssetTypeDialogOpen(true); }} className="gap-2 bg-indigo-600 hover:bg-indigo-700">
                <Plus size={16} /> Add Asset Type
              </Button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {assetTypes.map(type => {
                const IconComp = getIconComponent(type.icon);
                const assetCount = assets.filter(a => a.asset_type_id === type.id).length;
                return (
                  <Card key={type.id} className="hover:shadow-md transition-shadow">
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-center gap-3">
                          <div className="p-2 bg-indigo-100 rounded-lg">
                            <IconComp className="text-indigo-600" size={24} />
                          </div>
                          <div>
                            <h3 className="font-semibold">{type.name}</h3>
                            <p className="text-sm text-slate-500">{type.category}</p>
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => editAssetType(type)}>
                            <Edit2 size={14} />
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteAssetType(type)} className="text-red-500 hover:text-red-700">
                            <Trash2 size={14} />
                          </Button>
                        </div>
                      </div>
                      <div className="mt-3 pt-3 border-t flex items-center justify-between text-sm">
                        <span className="text-slate-500">{assetCount} assets</span>
                        <span className="text-slate-500">Depreciation: {type.depreciation_rate}%/yr</span>
                      </div>
                      {type.description && (
                        <p className="mt-2 text-sm text-slate-500">{type.description}</p>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
              {assetTypes.length === 0 && (
                <Card className="col-span-full">
                  <CardContent className="py-8 text-center text-slate-500">
                    No asset types defined. Add your first asset type to get started.
                  </CardContent>
                </Card>
              )}
            </div>
          </TabsContent>

          {/* Assignments Tab */}
          <TabsContent value="assignments" className="space-y-4">
            <Card>
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-lg">Asset Assignments</CardTitle>
                <div className="relative w-72">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
                  <Input
                    placeholder="Search asset or employee..."
                    value={assignmentSearch}
                    onChange={(e) => setAssignmentSearch(e.target.value)}
                    className="pl-9"
                    data-testid="assignment-search-input"
                  />
                </div>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset</TableHead>
                      <TableHead>Employee</TableHead>
                      <TableHead>Assigned Date</TableHead>
                      <TableHead>Expected Return</TableHead>
                      <TableHead>Returned Date</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-center">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {assignments
                      .filter(a => {
                        if (!assignmentSearch) return true;
                        const q = assignmentSearch.toLowerCase();
                        return (
                          a.asset_name?.toLowerCase().includes(q) ||
                          a.asset_serial?.toLowerCase().includes(q) ||
                          a.employee_name?.toLowerCase().includes(q) ||
                          a.employee_code?.toLowerCase().includes(q)
                        );
                      })
                      .map(assignment => (
                      <TableRow key={assignment.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{assignment.asset_name}</p>
                            <p className="text-sm text-slate-500 font-mono">{assignment.asset_serial}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div>
                            <p className="font-medium">{assignment.employee_name}</p>
                            {assignment.employee_code && <p className="text-sm text-slate-500">{assignment.employee_code}</p>}
                          </div>
                        </TableCell>
                        <TableCell>{assignment.assigned_date?.split('T')[0]}</TableCell>
                        <TableCell>{assignment.expected_return_date?.split('T')[0] || '-'}</TableCell>
                        <TableCell>{assignment.returned_date?.split('T')[0] || '-'}</TableCell>
                        <TableCell>
                          <Badge className={assignment.returned_date ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                            {assignment.returned_date ? 'Returned' : 'Active'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-center">
                          {!assignment.returned_date && (
                            <Button 
                              variant="outline" 
                              size="sm" 
                              onClick={() => { setSelectedAssignment(assignment); setReturnDialogOpen(true); }}
                              className="gap-1"
                            >
                              <UserX size={14} /> Return
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                    {assignments.filter(a => {
                      if (!assignmentSearch) return true;
                      const q = assignmentSearch.toLowerCase();
                      return a.asset_name?.toLowerCase().includes(q) || a.asset_serial?.toLowerCase().includes(q) || a.employee_name?.toLowerCase().includes(q) || a.employee_code?.toLowerCase().includes(q);
                    }).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                          {assignmentSearch ? 'No matching assignments found.' : 'No assignments found.'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Maintenance Tab */}
          <TabsContent value="maintenance" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Maintenance History</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Asset</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Vendor</TableHead>
                      <TableHead>Cost</TableHead>
                      <TableHead>Start Date</TableHead>
                      <TableHead>End Date</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {maintenanceRecords.map(record => (
                      <TableRow key={record.id}>
                        <TableCell className="font-medium">{record.asset_name}</TableCell>
                        <TableCell>{record.maintenance_type}</TableCell>
                        <TableCell className="max-w-xs truncate">{record.description}</TableCell>
                        <TableCell>{record.vendor || '-'}</TableCell>
                        <TableCell>{formatCurrency(record.cost)}</TableCell>
                        <TableCell>{record.start_date}</TableCell>
                        <TableCell>{record.end_date || '-'}</TableCell>
                        <TableCell>
                          <Badge className={record.end_date ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}>
                            {record.end_date ? 'Completed' : 'In Progress'}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                    {maintenanceRecords.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={8} className="text-center py-8 text-slate-500">
                          No maintenance records found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Asset Type Dialog */}
        <Dialog open={assetTypeDialogOpen} onOpenChange={setAssetTypeDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{editingAssetType ? 'Edit Asset Type' : 'Add Asset Type'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Name *</Label>
                  <Input
                    value={assetTypeForm.name}
                    onChange={(e) => setAssetTypeForm({ ...assetTypeForm, name: e.target.value })}
                    placeholder="e.g., Laptop"
                  />
                </div>
                <div>
                  <Label>Category *</Label>
                  <Select value={assetTypeForm.category} onValueChange={(v) => setAssetTypeForm({ ...assetTypeForm, category: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {DEFAULT_CATEGORIES.map(cat => (
                        <SelectItem key={cat.name} value={cat.name}>{cat.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Depreciation Rate (%/year)</Label>
                  <Input
                    type="number"
                    value={assetTypeForm.depreciation_rate}
                    onChange={(e) => setAssetTypeForm({ ...assetTypeForm, depreciation_rate: e.target.value })}
                    placeholder="10"
                  />
                </div>
                <div>
                  <Label>Icon</Label>
                  <Select value={assetTypeForm.icon} onValueChange={(v) => setAssetTypeForm({ ...assetTypeForm, icon: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {['Monitor', 'Laptop', 'Keyboard', 'Mouse', 'Headphones', 'Car', 'Building2', 'CreditCard', 'Package', 'Box'].map(icon => (
                        <SelectItem key={icon} value={icon}>{icon}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={assetTypeForm.description}
                  onChange={(e) => setAssetTypeForm({ ...assetTypeForm, description: e.target.value })}
                  placeholder="Optional description"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssetTypeDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveAssetType} className="bg-indigo-600 hover:bg-indigo-700">
                {editingAssetType ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Asset Dialog */}
        <Dialog open={assetDialogOpen} onOpenChange={setAssetDialogOpen}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingAsset ? 'Edit Asset' : 'Add New Asset'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Asset Type *</Label>
                  <Select value={assetForm.asset_type_id} onValueChange={(v) => setAssetForm({ ...assetForm, asset_type_id: v })}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {assetTypes.map(type => (
                        <SelectItem key={type.id} value={type.id}>{type.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Asset Name *</Label>
                  <Input
                    value={assetForm.name}
                    onChange={(e) => setAssetForm({ ...assetForm, name: e.target.value })}
                    placeholder="e.g., MacBook Pro 14"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Serial Number *</Label>
                  <Input
                    value={assetForm.serial_number}
                    onChange={(e) => setAssetForm({ ...assetForm, serial_number: e.target.value })}
                    placeholder="Unique serial number"
                    className="font-mono"
                  />
                </div>
                <div>
                  <Label>Brand</Label>
                  <Input
                    value={assetForm.brand}
                    onChange={(e) => setAssetForm({ ...assetForm, brand: e.target.value })}
                    placeholder="e.g., Apple"
                  />
                </div>
                <div>
                  <Label>Model</Label>
                  <Input
                    value={assetForm.model}
                    onChange={(e) => setAssetForm({ ...assetForm, model: e.target.value })}
                    placeholder="e.g., MBP-14-2024"
                  />
                </div>
              </div>
              
              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Purchase Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Purchase Date</Label>
                    <Input
                      type="date"
                      value={assetForm.purchase_date}
                      onChange={(e) => setAssetForm({ ...assetForm, purchase_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Purchase Price (₹)</Label>
                    <Input
                      type="number"
                      value={assetForm.purchase_price}
                      onChange={(e) => setAssetForm({ ...assetForm, purchase_price: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label>Vendor</Label>
                    <Input
                      value={assetForm.vendor}
                      onChange={(e) => setAssetForm({ ...assetForm, vendor: e.target.value })}
                      placeholder="Supplier name"
                    />
                  </div>
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label>Warranty Expiry</Label>
                  <Input
                    type="date"
                    value={assetForm.warranty_expiry}
                    onChange={(e) => setAssetForm({ ...assetForm, warranty_expiry: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Condition</Label>
                  <Select value={assetForm.condition} onValueChange={(v) => setAssetForm({ ...assetForm, condition: v })}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {CONDITION_OPTIONS.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Location</Label>
                  <Input
                    value={assetForm.location}
                    onChange={(e) => setAssetForm({ ...assetForm, location: e.target.value })}
                    placeholder="e.g., Office A, Floor 2"
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Insurance Information</h4>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Insurance Provider</Label>
                    <Input
                      value={assetForm.insurance_provider}
                      onChange={(e) => setAssetForm({ ...assetForm, insurance_provider: e.target.value })}
                      placeholder="Provider name"
                    />
                  </div>
                  <div>
                    <Label>Policy Number</Label>
                    <Input
                      value={assetForm.insurance_policy_number}
                      onChange={(e) => setAssetForm({ ...assetForm, insurance_policy_number: e.target.value })}
                      placeholder="Policy number"
                    />
                  </div>
                  <div>
                    <Label>Insurance Expiry</Label>
                    <Input
                      type="date"
                      value={assetForm.insurance_expiry}
                      onChange={(e) => setAssetForm({ ...assetForm, insurance_expiry: e.target.value })}
                    />
                  </div>
                </div>
              </div>

              <div>
                <Label>Notes</Label>
                <Textarea
                  value={assetForm.notes}
                  onChange={(e) => setAssetForm({ ...assetForm, notes: e.target.value })}
                  placeholder="Additional notes"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssetDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveAsset} className="bg-indigo-600 hover:bg-indigo-700">
                {editingAsset ? 'Update Asset' : 'Add Asset'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Assign Asset Dialog */}
        <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Assign Asset</DialogTitle>
            </DialogHeader>
            {selectedAsset && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedAsset.name}</p>
                  <p className="text-sm text-slate-500 font-mono">{selectedAsset.serial_number}</p>
                </div>
                <div>
                  <Label>Assign to Employee *</Label>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={16} />
                    <Input
                      type="text"
                      placeholder="Search employee by name or ID..."
                      value={assignForm.employee_search || ''}
                      onChange={(e) => setAssignForm({ ...assignForm, employee_search: e.target.value, employee_id: '' })}
                      className="pl-9"
                      data-testid="asset-assign-employee-search"
                    />
                    {assignForm.employee_id && (
                      <button
                        type="button"
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        onClick={() => setAssignForm({ ...assignForm, employee_search: '', employee_id: '' })}
                      >
                        <X size={16} />
                      </button>
                    )}
                  </div>
                  {assignForm.employee_id && (
                    <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                      <CheckCircle2 size={12} /> Employee selected
                    </p>
                  )}
                  {assignForm.employee_search && !assignForm.employee_id && (
                    <div className="mt-1 max-h-48 overflow-y-auto border rounded-lg bg-white shadow-lg">
                      {employees
                        .filter(emp => {
                          const search = (assignForm.employee_search || '').toLowerCase().replace(/[()]/g, ' ').trim();
                          const searchWords = search.split(/\s+/).filter(w => w.length > 0);
                          const fullName = `${emp.first_name || ''} ${emp.last_name || ''} ${emp.name || ''}`.toLowerCase();
                          const empId = (emp.employee_id || '').toLowerCase();
                          const empEmail = (emp.email || '').toLowerCase();
                          return searchWords.some(word => fullName.includes(word) || empId.includes(word) || empEmail.includes(word));
                        })
                        .slice(0, 10)
                        .map(emp => (
                          <div
                            key={emp.id}
                            className="p-2 hover:bg-blue-50 cursor-pointer border-b last:border-b-0"
                            onClick={() => setAssignForm({ 
                              ...assignForm, 
                              employee_id: emp.id, 
                              employee_search: `${emp.first_name || ''} ${emp.last_name || emp.name || ''} (${emp.employee_id})`.trim()
                            })}
                          >
                            <div className="font-medium text-sm">{emp.first_name || ''} {emp.last_name || emp.name || ''}</div>
                            <div className="text-xs text-slate-500">{emp.employee_id} {emp.email ? `• ${emp.email}` : ''} • {emp.designation || emp.department || 'N/A'}</div>
                          </div>
                        ))
                      }
                      {employees.filter(emp => {
                        const search = (assignForm.employee_search || '').toLowerCase().replace(/[()]/g, ' ').trim();
                        const searchWords = search.split(/\s+/).filter(w => w.length > 0);
                        const fullName = `${emp.first_name || ''} ${emp.last_name || ''} ${emp.name || ''}`.toLowerCase();
                        const empId = (emp.employee_id || '').toLowerCase();
                        const empEmail = (emp.email || '').toLowerCase();
                        return searchWords.some(word => fullName.includes(word) || empId.includes(word) || empEmail.includes(word));
                      }).length === 0 && (
                        <div className="p-3 text-center text-slate-500 text-sm">No employees found matching "{assignForm.employee_search}"</div>
                      )}
                    </div>
                  )}
                  {!assignForm.employee_search && !assignForm.employee_id && employees.length > 0 && (
                    <p className="text-xs text-slate-500 mt-1">Start typing to search from {employees.length} employees</p>
                  )}
                  {employees.length === 0 && (
                    <p className="text-xs text-amber-600 mt-1">No active employees found. Please check if employees exist in the system.</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Assigned Date</Label>
                    <Input
                      type="date"
                      value={assignForm.assigned_date}
                      onChange={(e) => setAssignForm({ ...assignForm, assigned_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Expected Return Date</Label>
                    <Input
                      type="date"
                      value={assignForm.expected_return_date}
                      onChange={(e) => setAssignForm({ ...assignForm, expected_return_date: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={assignForm.notes}
                    onChange={(e) => setAssignForm({ ...assignForm, notes: e.target.value })}
                    placeholder="Assignment notes"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setAssignDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleAssignAsset} className="bg-green-600 hover:bg-green-700 gap-2">
                <UserCheck size={16} /> Assign Asset
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Return Asset Dialog */}
        <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Return Asset</DialogTitle>
            </DialogHeader>
            {selectedAssignment && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedAssignment.asset_name}</p>
                  <p className="text-sm text-slate-500">Assigned to: {selectedAssignment.employee_name}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Return Date</Label>
                    <Input
                      type="date"
                      value={returnForm.return_date}
                      onChange={(e) => setReturnForm({ ...returnForm, return_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>Condition</Label>
                    <Select value={returnForm.condition} onValueChange={(v) => setReturnForm({ ...returnForm, condition: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {CONDITION_OPTIONS.map(c => (
                          <SelectItem key={c} value={c}>{c}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={returnForm.notes}
                    onChange={(e) => setReturnForm({ ...returnForm, notes: e.target.value })}
                    placeholder="Return notes (damage, missing items, etc.)"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleReturnAsset} className="bg-indigo-600 hover:bg-indigo-700 gap-2">
                <CheckCircle size={16} /> Confirm Return
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Maintenance Dialog */}
        <Dialog open={maintenanceDialogOpen} onOpenChange={setMaintenanceDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Maintenance Record</DialogTitle>
            </DialogHeader>
            {selectedAsset && (
              <div className="space-y-4 py-4">
                <div className="p-3 bg-slate-50 rounded-lg">
                  <p className="font-medium">{selectedAsset.name}</p>
                  <p className="text-sm text-slate-500 font-mono">{selectedAsset.serial_number}</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Maintenance Type</Label>
                    <Select value={maintenanceForm.maintenance_type} onValueChange={(v) => setMaintenanceForm({ ...maintenanceForm, maintenance_type: v })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Repair">Repair</SelectItem>
                        <SelectItem value="Service">Service</SelectItem>
                        <SelectItem value="Upgrade">Upgrade</SelectItem>
                        <SelectItem value="Inspection">Inspection</SelectItem>
                        <SelectItem value="Replacement">Replacement</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label>Vendor</Label>
                    <Input
                      value={maintenanceForm.vendor}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, vendor: e.target.value })}
                      placeholder="Service vendor"
                    />
                  </div>
                </div>
                <div>
                  <Label>Description *</Label>
                  <Textarea
                    value={maintenanceForm.description}
                    onChange={(e) => setMaintenanceForm({ ...maintenanceForm, description: e.target.value })}
                    placeholder="Describe the maintenance work"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label>Cost (₹)</Label>
                    <Input
                      type="number"
                      value={maintenanceForm.cost}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, cost: e.target.value })}
                      placeholder="0"
                    />
                  </div>
                  <div>
                    <Label>Start Date</Label>
                    <Input
                      type="date"
                      value={maintenanceForm.start_date}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, start_date: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label>End Date</Label>
                    <Input
                      type="date"
                      value={maintenanceForm.end_date}
                      onChange={(e) => setMaintenanceForm({ ...maintenanceForm, end_date: e.target.value })}
                    />
                  </div>
                </div>
                <div>
                  <Label>Notes</Label>
                  <Textarea
                    value={maintenanceForm.notes}
                    onChange={(e) => setMaintenanceForm({ ...maintenanceForm, notes: e.target.value })}
                    placeholder="Additional notes"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMaintenanceDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSaveMaintenance} className="bg-amber-600 hover:bg-amber-700 gap-2">
                <Wrench size={16} /> Add Record
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* View Asset Dialog */}
        <Dialog open={viewAssetDialogOpen} onOpenChange={setViewAssetDialogOpen}>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Asset Details</DialogTitle>
            </DialogHeader>
            {selectedAsset && (
              <div className="space-y-4 py-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Basic Information</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-500">Name:</span> {selectedAsset.name}</p>
                      <p><span className="text-slate-500">Type:</span> {getAssetTypeName(selectedAsset.asset_type_id)}</p>
                      <p><span className="text-slate-500">Serial:</span> <span className="font-mono">{selectedAsset.serial_number}</span></p>
                      <p><span className="text-slate-500">Brand:</span> {selectedAsset.brand || '-'}</p>
                      <p><span className="text-slate-500">Model:</span> {selectedAsset.model || '-'}</p>
                      <p><span className="text-slate-500">Condition:</span> {selectedAsset.condition}</p>
                      <p><span className="text-slate-500">Location:</span> {selectedAsset.location || '-'}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Financial Information</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-500">Purchase Date:</span> {selectedAsset.purchase_date || '-'}</p>
                      <p><span className="text-slate-500">Purchase Price:</span> {formatCurrency(selectedAsset.purchase_price)}</p>
                      <p><span className="text-slate-500">Vendor:</span> {selectedAsset.vendor || '-'}</p>
                      <p><span className="text-slate-500">Depreciation:</span> {formatCurrency(calculateDepreciation(selectedAsset))}</p>
                      <p><span className="text-slate-500">Current Value:</span> <span className="font-semibold text-green-600">{formatCurrency(getCurrentValue(selectedAsset))}</span></p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Warranty & Insurance</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-500">Warranty Expiry:</span> {selectedAsset.warranty_expiry || '-'}</p>
                      <p><span className="text-slate-500">Insurance Provider:</span> {selectedAsset.insurance_provider || '-'}</p>
                      <p><span className="text-slate-500">Policy Number:</span> {selectedAsset.insurance_policy_number || '-'}</p>
                      <p><span className="text-slate-500">Insurance Expiry:</span> {selectedAsset.insurance_expiry || '-'}</p>
                    </div>
                  </div>
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Assignment Status</h4>
                    <div className="space-y-1 text-sm">
                      <p><span className="text-slate-500">Status:</span> <Badge className={ASSET_STATUS[selectedAsset.status]?.color}>{ASSET_STATUS[selectedAsset.status]?.label}</Badge></p>
                      <p><span className="text-slate-500">Assigned To:</span> {selectedAsset.assigned_to_name || 'Not Assigned'}</p>
                    </div>
                  </div>
                </div>
                {selectedAsset.notes && (
                  <div className="p-4 bg-slate-50 rounded-lg">
                    <h4 className="font-semibold mb-2">Notes</h4>
                    <p className="text-sm">{selectedAsset.notes}</p>
                  </div>
                )}
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setViewAssetDialogOpen(false)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={bulkUploadOpen} onOpenChange={setBulkUploadOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Bulk Upload Assets</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <p className="text-sm text-slate-500">Upload an Excel file with asset details. Download the template first to see the required format.</p>
              <Button onClick={downloadTemplate} variant="outline" className="w-full gap-2">
                <Download size={16} /> Download Template
              </Button>
              <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                <Upload className="mx-auto h-8 w-8 text-slate-400 mb-2" />
                <p className="text-sm text-slate-600 mb-2">Select Excel file (.xlsx)</p>
                <label className="cursor-pointer">
                  <Input type="file" accept=".xlsx,.xls,.csv" onChange={handleBulkUpload} className="hidden" disabled={bulkUploading} />
                  <Button variant="outline" size="sm" disabled={bulkUploading} asChild>
                    <span>{bulkUploading ? 'Uploading...' : 'Choose File'}</span>
                  </Button>
                </label>
              </div>
              <p className="text-xs text-slate-400">Columns: Asset Name, Serial Number, Type, Model, Brand, Condition, Purchase Date, Purchase Price, Vendor, Warranty Expiry, Location, Notes</p>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AssetManagement;
